import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_X_KEY,
  CHAT_PIE_SIZE,
  ChatChartLayout,
  DisplayableChatChartSpec,
  TICK_FONT_PX,
  TILTED_LABEL_ANGLE,
  chartDataRows,
  chartSeriesKey,
  chartUnit,
  chartValueAt,
  drawablePieSlices,
  formatChartAxisTick,
  formatChartValue,
  truncateChartLabel,
} from 'src/app/chat/chatChartFormat';
import { PRINT_CHART_WIDTH, PrintChartPlan } from 'src/app/chat/printChartPlan';

export interface ITranscriptPrintChartPalette {
  dimmed: string;
  border: string;
  paper: string;
  chartColors: readonly string[];
}

const Swatch: React.FC<{ color: string }> = ({ color }) => (
  <svg width={10} height={10} aria-hidden="true" className="swatch">
    <rect width={10} height={10} rx={2} fill={color} />
  </svg>
);

const PrintPie: React.FC<{
  spec: DisplayableChatChartSpec;
  palette: ITranscriptPrintChartPalette;
}> = ({ spec, palette }) => {
  const unit = chartUnit(spec);
  const slices = spec.points.map((point, index) => ({
    name: point.x,
    value: chartValueAt(point, 0),
    color: palette.chartColors[index],
  }));
  const drawable = drawablePieSlices(slices);
  return (
    <div className="pie">
      <PieChart width={CHAT_PIE_SIZE} height={CHAT_PIE_SIZE}>
        <Pie
          data={drawable}
          dataKey="value"
          nameKey="name"
          outerRadius={CHAT_PIE_SIZE / 2 - 4}
          stroke={palette.paper}
          strokeWidth={1}
          isAnimationActive={false}
        >
          {drawable.map((slice, index) => (
            <Cell key={index} fill={slice.color} />
          ))}
        </Pie>
      </PieChart>
      <ul className="pie-legend">
        {slices.map((slice, index) => (
          <li key={index}>
            <span className="legend-name">
              <Swatch color={slice.color} />
              {slice.name}
            </span>
            <span className="num">{formatChartValue(slice.value, unit)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PrintCartesian: React.FC<{
  spec: DisplayableChatChartSpec;
  layout: ChatChartLayout;
  svgHeight: number;
  palette: ITranscriptPrintChartPalette;
}> = ({ spec, layout, svgHeight, palette }) => {
  const unit = chartUnit(spec);
  const data = chartDataRows(spec);
  const tick = { fontSize: TICK_FONT_PX, fill: palette.dimmed };
  const tickLine = { stroke: palette.border };
  const valueAxis = {
    type: 'number' as const,
    tickFormatter: (value: number) => formatChartAxisTick(value, unit),
    allowDecimals: !layout.integerTicks,
    axisLine: false,
    tickLine,
    tick,
  };
  const grid = (
    <CartesianGrid
      strokeDasharray="5 5"
      stroke={palette.border}
      vertical={layout.kind === 'rows'}
      horizontal={layout.kind !== 'rows'}
    />
  );
  const size = { width: PRINT_CHART_WIDTH, height: svgHeight, data };
  const color = (index: number) => palette.chartColors[index];

  const bars = spec.series.map((_, index) => (
    <Bar
      key={index}
      dataKey={chartSeriesKey(index)}
      fill={color(index)}
      stackId={spec.type === 'stacked-bar' ? 'stack' : undefined}
      isAnimationActive={false}
    />
  ));

  if (layout.kind === 'rows') {
    return (
      <BarChart {...size} layout="vertical">
        {grid}
        <XAxis {...valueAxis} />
        <YAxis
          type="category"
          dataKey={CHART_X_KEY}
          width={layout.categoryAxisWidth}
          interval={0}
          axisLine={false}
          tickLine={false}
          // Recharts word-wraps a tick given a width; the label is already cut to fit on one line.
          tick={{ ...tick, width: undefined }}
          tickFormatter={(label: string) =>
            truncateChartLabel(label, layout.labelMaxChars)
          }
        />
        {bars}
      </BarChart>
    );
  }

  const { tilt } = layout;
  const tiltedTick = {
    ...tick,
    angle: TILTED_LABEL_ANGLE,
    textAnchor: 'end' as const,
  };
  const categoryAxis = (
    <XAxis
      dataKey={CHART_X_KEY}
      interval="preserveStartEnd"
      minTickGap={5}
      axisLine={false}
      tickLine={false}
      {...(tilt
        ? {
            height: tilt.axisHeight,
            angle: TILTED_LABEL_ANGLE,
            tick: tiltedTick,
            tickFormatter: (label: string) =>
              truncateChartLabel(label, tilt.labelMaxChars),
          }
        : { tick })}
    />
  );
  const margin = { top: 5, right: 5, bottom: 5, left: tilt?.leftMargin ?? 5 };
  const axes = (
    <>
      {grid}
      {categoryAxis}
      <YAxis {...valueAxis} />
    </>
  );

  switch (spec.type) {
    case 'line':
      return (
        <LineChart {...size} margin={margin}>
          {axes}
          {spec.series.map((_, index) => (
            <Line
              key={index}
              type="linear"
              dataKey={chartSeriesKey(index)}
              stroke={color(index)}
              strokeWidth={2}
              dot={{ r: 3, fill: color(index), stroke: color(index) }}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      );
    case 'area':
      return (
        <AreaChart {...size} margin={margin}>
          {axes}
          {spec.series.map((_, index) => (
            <Area
              key={index}
              type="linear"
              dataKey={chartSeriesKey(index)}
              stroke={color(index)}
              strokeWidth={2}
              fill={color(index)}
              fillOpacity={0.2}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      );
    default:
      return (
        <BarChart {...size} margin={margin}>
          {axes}
          {bars}
        </BarChart>
      );
  }
};

/** @registry Fixed-size, unanimated recharts drawing of an AI chat chart for the isolated print iframe (no ResponsiveContainer, Tooltip, or CSS variables); `planPrintChart` decides whether it can be drawn at all. */
export const TranscriptPrintChart: React.FC<{
  plan: Extract<PrintChartPlan, { kind: 'pie' | 'cartesian' }>;
  palette: ITranscriptPrintChartPalette;
}> = ({ plan, palette }) => {
  if (plan.kind === 'pie') {
    return <PrintPie spec={plan.spec} palette={palette} />;
  }
  return (
    <>
      <PrintCartesian
        spec={plan.spec}
        layout={plan.layout}
        svgHeight={plan.svgHeight}
        palette={palette}
      />
      {plan.spec.series.length >= 2 && (
        <ul className="chart-legend">
          {plan.spec.series.map((series, index) => (
            <li key={index}>
              <Swatch color={palette.chartColors[index]} />
              {series.label}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
